/**
 * Util tanggal aman untuk iOS/Safari.
 * Safari sering gagal parse format non-ISO → Invalid Date → "NaN undefined NaN".
 */

export const parseDateSafe = (input) => {
  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? new Date() : input;
  }

  if (typeof input === "number") {
    const fromNumber = new Date(input);
    return Number.isNaN(fromNumber.getTime()) ? new Date() : fromNumber;
  }

  if (input == null || input === "") {
    return new Date();
  }

  let str = String(input).trim();

  // YYYY-MM-DD → local noon (hindari geser hari karena UTC di Safari)
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [y, m, d] = str.split("-").map(Number);
    return new Date(y, m - 1, d, 12, 0, 0);
  }

  // Ambil bagian tanggal dari datetime API: 2026-08-27T00:00:00.000Z
  const isoDateOnly = str.match(/^(\d{4}-\d{2}-\d{2})[T\s]/);
  if (isoDateOnly && !/[+-]\d{2}:?\d{2}$/.test(str) && str.includes("T00:00:00")) {
    // Tetap parse penuh jika ada Z/offset; untuk midnight UTC tampilkan hari lokal dari komponen tanggal
    const [y, m, d] = isoDateOnly[1].split("-").map(Number);
    // Jika string UTC midnight, gunakan tanggal kalender dari string (bukan lokal shift)
    if (/T00:00:00(\.0+)?Z$/i.test(str)) {
      return new Date(y, m - 1, d, 12, 0, 0);
    }
  }

  // YYYY-MM-DD HH:mm[:ss] → ganti spasi jadi T
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?$/.test(str)) {
    str = str.replace(" ", "T");
    const parsedLocal = new Date(str);
    if (!Number.isNaN(parsedLocal.getTime())) return parsedLocal;
  }

  // Perbaiki format lama getTimeKu: "08/27/2026, 08:02:15.123456 PM+08:00"
  const legacy = str.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4}),\s*(\d{1,2}):(\d{2}):(\d{2})(?:\.\d+)?\s*(AM|PM)?\s*([+-]\d{2}:?\d{2})?$/i
  );
  if (legacy) {
    let [, mm, dd, yyyy, hh, min, ss, ampm, tz] = legacy;
    let hour = Number(hh);
    if (ampm) {
      const upper = ampm.toUpperCase();
      if (upper === "PM" && hour < 12) hour += 12;
      if (upper === "AM" && hour === 12) hour = 0;
    }
    const iso = `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(
      2,
      "0"
    )}T${String(hour).padStart(2, "0")}:${min}:${ss}${tz || ""}`;
    const fromLegacy = new Date(iso);
    if (!Number.isNaN(fromLegacy.getTime())) return fromLegacy;
  }

  // MM/DD/YYYY atau M/D/YYYY
  const usDate = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (usDate) {
    const [, mm, dd, yyyy] = usDate;
    const fromUs = new Date(Number(yyyy), Number(mm) - 1, Number(dd), 12, 0, 0);
    if (!Number.isNaN(fromUs.getTime())) return fromUs;
  }

  const parsed = new Date(str);
  if (!Number.isNaN(parsed.getTime())) return parsed;

  return new Date();
};

/** Format lokal YYYY-MM-DD (jangan pakai toISOString di timezone +08) */
export const toYMD = (input) => {
  const d = parseDateSafe(input);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** Format tampilan: "27 Agustus 2026" */
export const formatTanggalID = (input, { short = false } = {}) => {
  const months = short
    ? [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "Mei",
        "Jun",
        "Jul",
        "Agu",
        "Sep",
        "Okt",
        "Nov",
        "Des",
      ]
    : [
        "Januari",
        "Februari",
        "Maret",
        "April",
        "Mei",
        "Juni",
        "Juli",
        "Agustus",
        "September",
        "Oktober",
        "November",
        "Desember",
      ];

  const d = parseDateSafe(input);
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

/** Normalisasi jam HH:mm[:ss] jadi HH:mm:ss untuk Date iOS */
export const normalizeTimeHMS = (t) => {
  if (!t) return "00:00:00";
  const parts = String(t).split(":");
  const hh = (parts[0] || "00").padStart(2, "0");
  const mm = (parts[1] || "00").padStart(2, "0");
  const ss = (parts[2] || "00").padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
};

export const parseDateTimeLocal = (dateYmd, timeHms) => {
  return parseDateSafe(`${toYMD(dateYmd)}T${normalizeTimeHMS(timeHms)}`);
};
